import { AiToolsList } from '../LandingPage';

import Markdown from 'react-markdown';
import { Button } from '~/components/ui/button';
import { Link } from '@remix-run/react';
import { getLanguagesList } from '~/services/languages';
import { ArrowRight } from 'lucide-react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '~/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import promptExamples from '~/services/prompt-examples.json';
import { BrowseIntegrationsButton } from '../LandingPage/components/Testimonials';
import { IntegrationsList } from '../ProductMicroLanding';

function Breadcrumbs({ category }) {
    return (
        <div className='py-4 bg-white'>
            <div className='px-4 mx-auto sm:px-6 lg:px-8 max-w-7xl'>
                <nav>
                    <ul className='flex items-center space-x-3'>
                        <li>
                            <a
                                href='/'
                                title='AI Perfect Assistant - Home Page'
                                className='text-gray-500 hover:text-gray-900'>
                                <svg
                                    className='w-5 h-5'
                                    xmlns='http://www.w3.org/2000/svg'
                                    fill='none'
                                    viewBox='0 0 24 24'
                                    stroke='currentColor'
                                    strokeWidth='2'>
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        d='M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
                                    />
                                </svg>
                            </a>
                        </li>

                        <li className='flex items-center space-x-3'>
                            <svg
                                className='flex-shrink-0 w-4 h-4 text-gray-500'
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                                strokeWidth='2'>
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='M9 5l7 7-7 7'
                                />
                            </svg>

                            <a
                                href='/tools'
                                title='Free AI Tools'
                                className='text-sm font-medium text-gray-500 hover:text-gray-900'>
                                {' '}
                                Free AI Tools{' '}
                            </a>
                        </li>

                        <li className='flex items-center space-x-3'>
                            <svg
                                className='flex-shrink-0 w-4 h-4 text-gray-500'
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                                strokeWidth='2'>
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='M9 5l7 7-7 7'
                                />
                            </svg>

                            <a
                                href={`/tools/${category}`}
                                title=''
                                className='text-sm font-medium text-gray-500 hover:text-gray-900 capitalize'>
                                {' '}
                                {category}{' '}
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    );
}

function Hero({ promptDefinition }) {
    return (
        <div className='relative'>
            <section className='relative bg-gray-900'>
                <div className='absolute inset-0'>
                    <div className='absolute inset-y-0 left-0 w-1/2 bg-white'></div>
                </div>

                <div className='relative mx-auto max-w-7xl lg:grid lg:grid-cols-2'>
                    <div>
                        <Breadcrumbs category={promptDefinition.type} />
                        <div className='flex items-center px-4 pb-16 bg-white pt-28 sm:px-6 lg:px-8 lg:pb-24 xl:pr-12'>
                            <div className='max-w-lg mx-auto lg:mx-0'>
                                <p className='text-5xl sm:text-6xl lg:text-7xl'>
                                    {promptDefinition.emoji}
                                </p>
                                <h1 className='mt-10 text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl'>
                                    {promptDefinition.seoTitle}
                                </h1>
                                <p className='mt-6 text-base font-normal leading-7 text-gray-500'>
                                    {promptDefinition.seoDescription}
                                </p>
                                <div className='mt-6'>
                                    <BrowseIntegrationsButton
                                        label={'Integrate with Your Apps'}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        id='demo'
                        className='relative flex items-end px-4 py-16 bg-gray-900 sm:px-6 lg:pb-24 lg:px-8 xl:pl-12'>
                        <div className='absolute inset-0'>
                            <img
                                className='object-cover w-full h-full'
                                src='https://landingfoliocom.imgix.net/store/collection/clarity-blog/images/hero/6/grid-pattern.svg'
                                alt=''
                            />
                        </div>

                        <div className='relative w-full max-w-lg mx-auto lg:max-w-none'>
                            <p className='text-lg font-bold text-white '>
                                {promptDefinition.seoTitle}
                            </p>

                            <div className='mt-6 space-y-5'>
                                <iframe
                                    className='w-full aspect-[3/4]  lg:aspect-[1/1] rounded-lg'
                                    title='AI Perfect Assistant Demo'
                                    src={`/iframe/${promptDefinition.id}`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function HowToSection({ promptDefinition }) {
    return (
        <section className='py-10 bg-white sm:py-16 lg:py-24'>
            <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                <div className='max-w-2xl mx-auto text-center'>
                    <h2 className='text-3xl font-bold leading-tight text-black sm:text-4xl lg:text-5xl'>
                        How to use the {promptDefinition.seoTitle}?
                    </h2>
                    <p className='max-w-lg mx-auto mt-4 text-base leading-relaxed text-gray-600'>
                        A brief tutorial on how-to use our{' '}
                        {promptDefinition.seoTitle} tool.
                    </p>
                </div>

                <div className='relative mt-12 lg:mt-20'>
                    <div className='absolute inset-x-0 hidden xl:px-44 top-2 md:block md:px-20 lg:px-28'>
                        <img
                            className='w-full'
                            src='https://cdn.rareblocks.xyz/collection/celebration/images/steps/2/curved-dotted-line.svg'
                            alt=''
                        />
                    </div>

                    <div className='relative grid grid-cols-1 text-center gap-y-12 md:grid-cols-3 gap-x-12'>
                        <div>
                            <div className='flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow'>
                                <span className='text-xl font-semibold text-gray-700'>
                                    {' '}
                                    1{' '}
                                </span>
                            </div>
                            <h3 className='mt-6 text-xl font-semibold leading-tight text-black md:mt-10'>
                                Fill out "{promptDefinition.textLabel}"
                            </h3>
                            <p className='mt-4 text-base text-gray-600'>
                                For example, you can start with something like "
                                {promptDefinition.example.length > 256
                                    ? `${promptDefinition.example.slice(0, 256)}...`
                                    : promptDefinition.example}
                                "
                            </p>
                        </div>

                        <div>
                            <div className='flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow'>
                                <span className='text-xl font-semibold text-gray-700'>
                                    {' '}
                                    2{' '}
                                </span>
                            </div>
                            <h3 className='mt-6 text-xl font-semibold leading-tight text-black md:mt-10'>
                                Tune tone and language
                            </h3>
                            <p className='mt-4 text-base text-gray-600'>
                                Set a preferred tone for the result and
                                language. We are fans of a "casual" tone.
                            </p>
                        </div>

                        <div>
                            <div className='flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow'>
                                <span className='text-xl font-semibold text-gray-700'>
                                    {' '}
                                    3{' '}
                                </span>
                            </div>
                            <h3 className='mt-6 text-xl font-semibold leading-tight text-black md:mt-10'>
                                Click "Generate with AI"
                            </h3>
                            <p className='mt-4 text-base text-gray-600'>
                                That's it! You'll get three highly relevant
                                results to select from, generated for you by the
                                top AI model.
                            </p>
                        </div>
                    </div>
                </div>
                <div className='flex items-center justify-center m-4'>
                    <Button asChild size={'lg'}>
                        <Link to='#demo'>
                            Get Started <ArrowRight />
                        </Link>
                    </Button>
                </div>
            </div>
        </section>
    );
}

export function FaqSection({ promptDefinition }) {
    return (
        <section className='py-12 bg-gray-50 sm:py-16 lg:py-20'>
            <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                <div className='text-center'>
                    <h2 className='text-3xl font-bold text-gray-900 sm:text-4xl xl:text-5xl font-pj'>
                        Frequently Asked Questions
                    </h2>
                </div>

                <div className='max-w-5xl mx-auto mt-10 sm:mt-20'>
                    <div className='flow-root'>
                        <div className='-my-8 divide-y divide-gray-200'>
                            <Accordion type='single' collapsible>
                                <AccordionItem value='item-1'>
                                    <AccordionTrigger>
                                        <p className='text-2xl font-bold text-gray-900 font-pj'>
                                            01. What is{' '}
                                            {promptDefinition.seoTitle}?
                                        </p>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <p className='mt-8 text-lg font-normal text-gray-600 font-pj'>
                                            {promptDefinition.seoTitle} is a
                                            tool to{' '}
                                            {promptDefinition.description}
                                        </p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value='item-2'>
                                    <AccordionTrigger>
                                        <p className='text-2xl font-bold text-gray-900 font-pj'>
                                            02. How do I use{' '}
                                            {promptDefinition.seoTitle}?
                                        </p>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <p className='mt-8 text-lg font-normal text-gray-600 font-pj'>
                                            1. Fill out "
                                            {promptDefinition.textLabel}"
                                            <br />
                                            2. Tune tone and language
                                            <br />
                                            3. Click "Generate with AI"
                                        </p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value='item-3'>
                                    <AccordionTrigger>
                                        <p className='text-2xl font-bold text-gray-900 font-pj'>
                                            03. Can I use an advanced AI model
                                            with {promptDefinition.seoTitle}?
                                        </p>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <p className='mt-8 text-lg font-normal text-gray-600 font-pj'>
                                            Yes! GPT-4 Omega is enabled by
                                            default for all registered users.{' '}
                                            <a
                                                href='/iframe/signin?source=faq'
                                                className='underline'>
                                                Simply sign up for an account
                                            </a>{' '}
                                            and start using the advanced AI
                                            model right away.
                                        </p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value='item-4'>
                                    <AccordionTrigger>
                                        <p className='text-2xl font-bold text-gray-900 font-pj'>
                                            04. What languages does the{' '}
                                            {promptDefinition.seoTitle} support?
                                        </p>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <p className='mt-8 text-lg font-normal text-gray-600 font-pj'>
                                            {promptDefinition.seoTitle} supports
                                            more than{' '}
                                            {getLanguagesList().length -
                                                (getLanguagesList().length %
                                                    10)}{' '}
                                            languages. Full list of supported
                                            languages:{' '}
                                            {getLanguagesList()
                                                .map((l) => l.label)
                                                .join(', ')}
                                        </p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value='item-5'>
                                    <AccordionTrigger>
                                        <p className='text-2xl font-bold text-gray-900 font-pj'>
                                            05. Is there a limit on number of
                                            requests to{' '}
                                            {promptDefinition.seoTitle}?
                                        </p>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <p className='mt-8 text-lg font-normal text-gray-600 font-pj'>
                                            Yes, in the public version, you can
                                            make up to 5 free queries. After
                                            that, you'll need to{' '}
                                            <a
                                                href='/iframe/signin?source=faq'
                                                className='underline'>
                                                sign up
                                            </a>{' '}
                                            to continue using the service.
                                        </p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value='item-6'>
                                    <AccordionTrigger>
                                        <p className='text-2xl font-bold text-gray-900 font-pj'>
                                            06. Where can I use{' '}
                                            {promptDefinition.seoTitle}?
                                        </p>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <p className='mt-8 text-lg font-normal text-gray-600 font-pj'>
                                            We integrated{' '}
                                            {promptDefinition.seoTitle} to Word,
                                            Excel, Teams, PowerPoint, Telegram,
                                            Chrome & Gmail. Also we have
                                            web-apps for Windows, Android & IOS.
                                            Full list of integrations you can
                                            find{' '}
                                            <a
                                                href='/integrations'
                                                className='underline'>
                                                here
                                            </a>
                                            .
                                        </p>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function ExampleSection({ promptDefinition }) {
    const examples = promptExamples[promptDefinition.id];
    if (!examples) {
        return null;
    }

    return (
        <section className='py-10 bg-white sm:py-16 lg:py-24'>
            <div className='px-4 mx-auto max-w-7xl sm:px-6 lg:px-8'>
                <div className='max-w-2xl mx-auto text-center'>
                    <h2 className='text-3xl font-bold leading-tight text-black sm:text-4xl lg:text-5xl'>
                        {promptDefinition.title} Example
                    </h2>
                    <p className='max-w-lg mx-auto mt-4 text-base leading-relaxed text-gray-600'>
                        A quick example to demonstrate how&nbsp;
                        {promptDefinition.seoTitle} works.
                    </p>
                </div>

                <div className='relative flex flex-col gap-4'>
                    <h3 className='text-2xl font-bold text-gray-900 font-pj'>
                        Input
                    </h3>
                    <blockquote className='mt-6 border-l-2 pl-6 italic'>
                        {examples.input}
                    </blockquote>

                    <h3 className='text-2xl font-bold text-gray-900 font-pj'>
                        Result
                    </h3>
                    <Tabs defaultValue='1' className='w-full max-w-prose'>
                        <TabsList className='grid w-full grid-cols-3 max-w-[300px] mb-4'>
                            <TabsTrigger value='1'>Option 1</TabsTrigger>
                            <TabsTrigger value='2'>Option 2</TabsTrigger>
                            <TabsTrigger value='3'>Option 3</TabsTrigger>
                        </TabsList>
                        <TabsContent value='1'>
                            <Markdown>{examples.result[0]}</Markdown>
                        </TabsContent>
                        <TabsContent value='2'>
                            <Markdown>{examples.result[1]}</Markdown>
                        </TabsContent>
                        <TabsContent value='3'>
                            <Markdown>{examples.result[2]}</Markdown>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </section>
    );
}

export default function Index({ promptDefinition }) {
    return (
        <>
            <Hero promptDefinition={promptDefinition} />
            <HowToSection promptDefinition={promptDefinition} />
            <IntegrationsList title={`Use ${promptDefinition.seoTitle} with`} />
            <ExampleSection promptDefinition={promptDefinition} />
            <FaqSection promptDefinition={promptDefinition} />
            <AiToolsList title='Browse other Free AI Tools' />
        </>
    );
}
